import { useState, useEffect } from 'react';
import { useFormik, FieldArray, FormikProvider, FormikErrors } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { Form, Modal, Row, Col } from 'react-bootstrap';
import { KTSVG } from '../../../../_metronic/helpers';
import axios from 'axios';
import { useAuth } from '../../auth/core/Auth';

const relations = [
  "Mother",
  "Father",
  "Spouse",
  "Partner",
  "Brother",
  "Sister",
  "Son",
  "Daughter",
  "Mother-In-Law",
  "Brother-In-Law",
  "Father-In-Law",
  "Sister-In-Law",
  "Guardian"
];

const nomineeSchema = Yup.object().shape({
  nominees: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required('Name is required'),
      dob: Yup.date()
        .required('Date of Birth is required')
        .when('relation', {
          is: 'Spouse',
          then: (schema) =>
            schema.test('spouse-min-age', 'Spouse must be at least 18 years old', (value) => {
              if (!value) return false;
              const today = new Date();
              const dob = new Date(value);
              let age = today.getFullYear() - dob.getFullYear();
              const m = today.getMonth() - dob.getMonth();
              if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                age--;
              }
              return age >= 18;
            }),
        }),
      relation: Yup.string().required('Relation is required'),
      gender: Yup.string().required('Gender is required'),
      allocation: Yup.number()
        .min(0, 'Minimum 0%')
        .max(100, 'Maximum 100%')
        .required('Allocation is required'),
    })
  ).test('total-allocation', 'Total allocation must be 100%', (nominees) => {
    if (!nominees) return false;
    const total = nominees.reduce((sum, nominee) => sum + (nominee.allocation || 0), 0);
    return total === 100;
  }),
});

type Props = {
  show: boolean;
  handleClose: () => void;
};

type Nominee = {
  Id?: number;
  NomineeName: string;
  NomineeDob: string;
  NomineeRelation: string;
  NomineeGender: string;
  NomineeAllocation: number;
  Freez: boolean;
};

const NomineeModal: React.FC<Props> = ({ show, handleClose }) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nomineeData, setNomineeData] = useState<Nominee[]>([]);
  const { currentUser } = useAuth(); // Get currentUser from useAuth

  const fetchData = async () => {
    try {
      const response = await axios.post('https://etl.futurisk.in/webhook/46efbdb2-7615-46f3-a622-7c12b3a8a23a-get', {
        params: {
          EmployeeId: currentUser?.EmployeeId,
          CrmClientId: currentUser?.CRMClientId,
        },
      });
      setNomineeData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data', error);
      setError('Something went wrong while fetching data.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentUser]);

  const initialValues: { nominees: { id?: number; name: string; dob: string; relation: string; gender: string; allocation: number; }[] } = {
    nominees: nomineeData.filter(nominee => !nominee.Freez).map(nominee => ({
      id: nominee.Id,
      name: nominee.NomineeName,
      dob: nominee.NomineeDob.split('T')[0],
      relation: nominee.NomineeRelation,
      gender: nominee.NomineeGender,
      allocation: nominee.NomineeAllocation,
    })),
  };

  const formik = useFormik({
    initialValues,
    validationSchema: nomineeSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          values: values.nominees.map(nominee => ({
            ...nominee,
            Id: nominee.id || undefined,
          })),
          EmployeeId: currentUser?.EmployeeId,
          CrmClientId: currentUser?.CRMClientId,
          LoginId: currentUser?.UserName,
        };
        await axios.post('https://etl.futurisk.in/webhook/77303646-7691-417a-b279-eaf16cfaa415-post', payload);
        setIsSubmitted(true);
        await fetchData(); // Fetch the updated data after submission
        // Reload the page after a short delay so the user sees the success message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } catch (error) {
        console.error('Error submitting form', error);
        setError('Something went wrong while submitting the form.');
      }
    },
  });

  useEffect(() => {
    if (nomineeData.length > 0) {
      formik.setValues({
        nominees: nomineeData.filter(nominee => !nominee.Freez).map(nominee => ({
          id: nominee.Id,
          name: nominee.NomineeName,
          dob: nominee.NomineeDob.split('T')[0],
          relation: nominee.NomineeRelation,
          gender: nominee.NomineeGender,
          allocation: nominee.NomineeAllocation,
        })),
      });
    }
  }, [nomineeData]);

  const getNomineeError = (index: number, field: keyof { name: string; dob: string; relation: string; gender: string; allocation: number; }) => {
    const errors = formik.errors.nominees as FormikErrors<{ name: string; dob: string; relation: string; gender: string; allocation: number; }>[]; 
    return errors?.[index]?.[field];
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_nominee'
      data-backdrop='static'
      data-keyboard='false'
      tabIndex={-1}
      role='dialog'
      show={show}
      dialogClassName=''
      aria-hidden='true'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Nominee Details</h5>
          <div
            className='btn btn-icon btn-sm btn-active-light-primary ms-2'
            onClick={handleClose}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>
        </div>
        <div className='modal-body'>
        <div style={{
              backgroundColor: '#fff8e1',
              border: '1px solid #fbc02d',
              padding: '16px 24px',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              lineHeight: '1.6',
              borderRadius: '8px',
              marginBottom: '24px',
              color: '#333'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Disclaimer:</div>
              <ul style={{
                paddingLeft: '20px',
                margin: 0,
                listStyleType: 'disc'
              }}>
                <li style={{ marginBottom: '6px' }}>
                The nominees selected for the GPA/GTL will also be the nominees for the Hope Beyond Life program.
                </li>
                
              </ul>
            </div>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}
              {isSubmitted ? (
                <div className="alert alert-success" role="alert">
                  Nominee updated successfully!
                </div>
              ) : null}
              {nomineeData.some(nominee => nominee.Freez) || isSubmitted ? (
                nomineeData.map((nominee, index) => (
                  <div key={index} className="card mb-3">
                    <div className="card-body">
                      <h5 className="card-title">{nominee.NomineeName}</h5>
                      <p className="card-text">Date of Birth: {nominee.NomineeDob.split('T')[0]}</p>
                      <p className="card-text">Relation: {nominee.NomineeRelation}</p>
                      <p className="card-text">Gender: {nominee.NomineeGender}</p>
                      <p className="card-text">Allocation: {nominee.NomineeAllocation}%</p>
                    </div>
                  </div>
                ))
              ) : (
                <FormikProvider value={formik}>
                  <form onSubmit={formik.handleSubmit}>
                    <FieldArray name="nominees">
                      {({ push, remove }) => (
                        <>
                          {formik.values.nominees.map((_, index) => (
                            <div key={index} className="nominee-form-group">
                              <Row className="mb-7">
                                <Col>
                                  <label className="form-label fw-bolder text-dark fs-6">Name</label>
                                  <input
                                    type="text"
                                    {...formik.getFieldProps(`nominees.${index}.name`)}
                                    className={clsx(
                                      'form-control form-control-lg form-control-solid',
                                      {
                                        'is-invalid': formik.touched.nominees?.[index]?.name && getNomineeError(index, 'name'),
                                      },
                                      {
                                        'is-valid': formik.touched.nominees?.[index]?.name && !getNomineeError(index, 'name'),
                                      }
                                    )}
                                  />
                                  {formik.touched.nominees?.[index]?.name && getNomineeError(index, 'name') && (
                                    <div className="fv-plugins-message-container">
                                      <div className="fv-help-block">
                                        <span role="alert">{getNomineeError(index, 'name')}</span>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              <Row className="mb-7">
                                <Col>
                                  <label className="form-label fw-bolder text-dark fs-6">Date of Birth</label>
                                  <input
                                    type="date"
                                    {...formik.getFieldProps(`nominees.${index}.dob`)}
                                    className={clsx(
                                      'form-control form-control-lg form-control-solid',
                                      {
                                        'is-invalid': formik.touched.nominees?.[index]?.dob && getNomineeError(index, 'dob'),
                                      },
                                      {
                                        'is-valid': formik.touched.nominees?.[index]?.dob && !getNomineeError(index, 'dob'),
                                      }
                                    )}
                                  />
                                  {formik.touched.nominees?.[index]?.dob && getNomineeError(index, 'dob') && (
                                    <div className="fv-plugins-message-container">
                                      <div className="fv-help-block">
                                        <span role="alert">{getNomineeError(index, 'dob')}</span>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              <Row className="mb-7">
                                <Col>
                                  <label className="form-label fw-bolder text-dark fs-6">Relation</label>
                                  <Form.Select
                                    {...formik.getFieldProps(`nominees.${index}.relation`)}
                                    className={clsx(
                                      'form-select form-select-solid form-select-lg fw-bold',
                                      {
                                        'is-invalid': formik.touched.nominees?.[index]?.relation && getNomineeError(index, 'relation'),
                                      },
                                      {
                                        'is-valid': formik.touched.nominees?.[index]?.relation && !getNomineeError(index, 'relation'),
                                      }
                                    )}
                                  >
                                    <option value="">Select Relation</option>
                                    {relations.map((relation) => (
                                      <option key={relation} value={relation}>
                                        {relation}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  {formik.touched.nominees?.[index]?.relation && getNomineeError(index, 'relation') && (
                                    <div className="fv-plugins-message-container">
                                      <div className="fv-help-block">
                                        <span role="alert">{getNomineeError(index, 'relation')}</span>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              <Row className="mb-7">
                                <Col>
                                  <label className="form-label fw-bolder text-dark fs-6">Gender</label>
                                  <Form.Select
                                    {...formik.getFieldProps(`nominees.${index}.gender`)}
                                    className={clsx(
                                      'form-select form-select-solid form-select-lg fw-bold',
                                      {
                                        'is-invalid': formik.touched.nominees?.[index]?.gender && getNomineeError(index, 'gender'),
                                      },
                                      {
                                        'is-valid': formik.touched.nominees?.[index]?.gender && !getNomineeError(index, 'gender'),
                                      }
                                    )}
                                  >
                                    <option value="">Select Gender</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Others">Others</option>
                                  </Form.Select>
                                  {formik.touched.nominees?.[index]?.gender && getNomineeError(index, 'gender') && (
                                    <div className="fv-plugins-message-container">
                                      <div className="fv-help-block">
                                        <span role="alert">{getNomineeError(index, 'gender')}</span>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              <Row className="mb-7">
                                <Col>
                                  <label className="form-label fw-bolder text-dark fs-6">% of Allocation</label>
                                  <input
                                    type="number"
                                    {...formik.getFieldProps(`nominees.${index}.allocation`)}
                                    className={clsx(
                                      'form-control form-control-lg form-control-solid',
                                      {
                                        'is-invalid': formik.touched.nominees?.[index]?.allocation && getNomineeError(index, 'allocation'),
                                      },
                                      {
                                        'is-valid': formik.touched.nominees?.[index]?.allocation && !getNomineeError(index, 'allocation'),
                                      }
                                    )}
                                  />
                                  {formik.touched.nominees?.[index]?.allocation && getNomineeError(index, 'allocation') && (
                                    <div className="fv-plugins-message-container">
                                      <div className="fv-help-block">
                                        <span role="alert">{getNomineeError(index, 'allocation')}</span>
                                      </div>
                                    </div>
                                  )}
                                </Col>
                              </Row>

                              {index > 0 && (
                                <button type="button" onClick={() => remove(index)} className="btn btn-danger mb-7">
                                  Remove Nominee
                                </button>
                              )}
                            </div>
                          ))}
                          {formik.values.nominees.length < 4 && (
                            <button type="button" onClick={() => push({ id: null, name: '', dob: '', relation: '', gender: '', allocation: 0 })} className="btn btn-primary mb-7">
                              Add Nominee
                            </button>
                          )}
                        </>
                      )}
                    </FieldArray>

                    <div className="text-center">
                      <button type="submit" className="btn btn-lg btn-primary w-100 mb-5" disabled={!formik.isValid || formik.isSubmitting}>
                        Submit
                      </button>
                    </div>
                  </form>
                </FormikProvider>
              )}
            </>
          )}
        </div>
        <div className='modal-footer'>
          * terms and conditions apply
        </div>
      </div>
    </Modal>
  );
};

export { NomineeModal };
